// Pure helpers for the tempo-drill timing logic.
// Isolated so the drill state machine (in `use-tempo-drill`) stays focused
// on React orchestration while these timing computations remain trivially
// testable.

import { applyInversion } from "@/lib/music/chord-catalog";
import { pitchClassToName } from "@/lib/music/theory-helpers";

export type TempoLevel = {
  bpm: number;
  label: string;
  tier: string;
  color: string;
};

export type EventType = "hit" | "perfect" | "miss";
export type MissReason = "no_press" | "early" | "late" | "wrong_chord";

/**
 * A fresh chord press captured straight from the piano events: the moment a
 * new pitch class was added, with the full content held at that instant.
 * Content is validated at settlement, against the chord window around the
 * downbeat the press is credited to, NOT at press time: the required chord
 * flips exactly on the downbeat while the timing tolerance straddles it, so
 * validating at press time judges boundary presses against the wrong
 * inversion.
 */
export type ChordPress = {
  /** `performance.now()` timestamp of the MIDI press event. */
  time: number;
  /** Distinct pitch classes held at the moment of the press. */
  pcs: readonly number[];
  /** Pitch class of the lowest held MIDI note. */
  bassPc: number;
};

export type LastResult =
  | { type: "hit"; ts: number; offsetMs: number }
  | { type: "perfect"; ts: number; offsetMs: number }
  | { type: "miss"; ts: number; reason: MissReason; offsetMs: number | null };

export type SlotData = {
  inv: number;
  notes: string[];
  pcs: number[];
  bassName: string;
  label: string;
};

/** Normalizes a MIDI note (or pitch offset) to a pitch class 0..11. */
export function toPc(value: number): number {
  return ((value % 12) + 12) % 12;
}

/** Beats per drill measure; also the length of the count-in. */
export const BEATS_PER_MEASURE = 4;

/** Inter-chord pixel offset bounds. Actual STEP is computed responsively
 *  per viewport width (see useResponsiveStep). */
export const STEP_MIN = 160;
export const STEP_MAX = 320;
/** Fraction of viewport width used as the responsive STEP target. */
export const STEP_VW_FACTOR = 0.18;
export const SLIDE_MS = 520;
export const FEEDBACK_VISIBLE_MS = 800;
export const HISTORY_LENGTH = 12;
export const HINT_BASE_OCTAVE = 3;
/** Tolerance window around the downbeat for a fresh press to count (ms). */
export const TIMING_TOLERANCE_MS = 250;
/** Tighter window inside which a hit is upgraded to "perfect". */
export const PERFECT_TOLERANCE_MS = 60;

export const INVERSION_LABELS = ["Fond.", "1er", "2e", "3e"] as const;

/**
 * Splits a queue of press timestamps around a midpoint between the previous
 * and current downbeats. Presses earlier than the midpoint are attributed
 * to the previous measure; later ones are kept for the next settlement.
 */
export function splitPressesAtMidpoint(
  queue: readonly ChordPress[],
  midpoint: number,
): { previous: ChordPress[]; next: ChordPress[] } {
  const previous: ChordPress[] = [];
  const next: ChordPress[] = [];

  for (const press of queue) {
    if (press.time < midpoint) {
      previous.push(press);
    } else {
      next.push(press);
    }
  }

  return { previous, next };
}

/**
 * From a list of presses, returns the one closest in time to `target`.
 * That is the user's best attempt at playing on the target downbeat.
 */
export function findClosestPress(
  presses: readonly ChordPress[],
  target: number,
): ChordPress | null {
  if (presses.length === 0) {
    return null;
  }

  let best = presses[0];
  for (const press of presses) {
    if (Math.abs(press.time - target) < Math.abs(best.time - target)) {
      best = press;
    }
  }

  return best;
}

/**
 * True when the press content satisfies the chord: every required pitch
 * class held, and the lowest held note is the expected bass. The bass is
 * what distinguishes inversions, their pc sets are identical.
 */
export function pressMatchesChord(
  press: ChordPress,
  requiredPcs: readonly number[],
  expectedBassPc: number,
): boolean {
  if (press.bassPc !== expectedBassPc) {
    return false;
  }

  return requiredPcs.every((pc) => press.pcs.includes(pc));
}

/**
 * Computes the outcome (perfect/hit/miss + reason + offset) of a measure
 * given the relevant press timestamp and the audio downbeat anchor.
 *
 * - `perfect` : press within ±perfectMs (tighter window)
 * - `hit`     : press within ±toleranceMs but outside the perfect window
 * - `miss`    : no press, or outside the tolerance window
 */
export function classifyOutcome(
  relevantPress: number | null,
  downbeatTime: number | null,
  toleranceMs: number,
  perfectMs: number,
): {
  offset: number | null;
  succeeded: boolean;
  perfect: boolean;
  reason: MissReason;
} {
  const offset =
    relevantPress !== null && downbeatTime !== null
      ? relevantPress - downbeatTime
      : null;
  const succeeded = offset !== null && Math.abs(offset) <= toleranceMs;
  const perfect = offset !== null && Math.abs(offset) <= perfectMs;
  const reason: MissReason =
    offset === null ? "no_press" : offset < 0 ? "early" : "late";

  return { offset, succeeded, perfect, reason };
}

/** The two inversions adjacent to a downbeat: `incoming` arrives on it,
 *  `outgoing` is the one whose measure ends there. */
export type ChordWindow = {
  incoming: readonly number[];
  outgoing: readonly number[];
};

export type MeasureSettlement = {
  result: LastResult;
  /** Presses past the midpoint, kept for the next settlement. */
  nextQueue: ChordPress[];
};

/**
 * Judges one drill measure. Splits the press queue at the midpoint between
 * the two downbeats (later presses are kept for the next settlement), then
 * classifies the press closest to `prevDownbeat` among those matching
 * either inversion adjacent to it. Both readings of the drill are accepted
 * — playing the chord as it arrives on the downbeat, or playing the
 * displayed chord on the click that closes its measure — so a press landing
 * 1 ms on either side of the click is never judged against the wrong
 * inversion.
 *
 * `now` is the wall-clock (Date.now) stamped on the result for UI flashes.
 */
export function settleMeasure(
  queue: readonly ChordPress[],
  prevDownbeat: number,
  downbeat: number,
  window: ChordWindow,
  now: number,
): MeasureSettlement {
  const midpoint = (prevDownbeat + downbeat) / 2;
  const { previous, next } = splitPressesAtMidpoint(queue, midpoint);
  const matching = previous.filter(
    (p) =>
      pressMatchesChord(p, window.incoming, window.incoming[0]) ||
      pressMatchesChord(p, window.outgoing, window.outgoing[0]),
  );
  const relevant = findClosestPress(matching, prevDownbeat);
  const { offset, succeeded, perfect, reason } = classifyOutcome(
    relevant !== null ? relevant.time : null,
    prevDownbeat,
    TIMING_TOLERANCE_MS,
    PERFECT_TOLERANCE_MS,
  );

  let result: LastResult;
  if (perfect && offset !== null) {
    result = { type: "perfect", ts: now, offsetMs: offset };
  } else if (succeeded && offset !== null) {
    result = { type: "hit", ts: now, offsetMs: offset };
  } else {
    result = {
      type: "miss",
      ts: now,
      reason:
        reason === "no_press" && previous.length > 0 ? "wrong_chord" : reason,
      offsetMs: offset,
    };
  }

  return { result, nextQueue: next };
}

/** Running drill score, folded one measure result at a time. */
export type Scoreboard = {
  combo: number;
  bestCombo: number;
  hits: number;
  misses: number;
  recentEvents: readonly EventType[];
  lastResult: LastResult | null;
};

export const EMPTY_SCOREBOARD: Scoreboard = {
  combo: 0,
  bestCombo: 0,
  hits: 0,
  misses: 0,
  recentEvents: [],
  lastResult: null,
};

/** Folds one measure result into the scoreboard (pure reducer). */
export function applyResult(board: Scoreboard, result: LastResult): Scoreboard {
  const succeeded = result.type !== "miss";
  const combo = succeeded ? board.combo + 1 : 0;

  return {
    combo,
    bestCombo: Math.max(board.bestCombo, combo),
    hits: board.hits + (succeeded ? 1 : 0),
    misses: board.misses + (succeeded ? 0 : 1),
    recentEvents: [
      ...board.recentEvents.slice(-(HISTORY_LENGTH - 1)),
      result.type,
    ],
    lastResult: result,
  };
}

/**
 * Builds the queue of 4 slots [outgoing, current, next, after] used by the
 * sliding-queue UI. Pure computation from the current position index.
 */
export function buildSlotQueue(
  posIdx: number,
  positions: number,
  intervals: number[],
  rootPc: number,
  rootSpelling: string,
): SlotData[] {
  return [-1, 0, 1, 2].map((offset) => {
    const rawIdx = posIdx + offset;
    const idx = ((rawIdx % positions) + positions) % positions;
    const semis = applyInversion(intervals, idx);
    const pcs = semis.map((s) => toPc(rootPc + s));
    const noteNames = pcs.map((pc) => pitchClassToName(pc, rootSpelling));

    return {
      inv: idx,
      notes: noteNames,
      pcs,
      bassName: noteNames[0],
      label: INVERSION_LABELS[idx] ?? `${idx}`,
    };
  });
}

export function formatTempoFeedback(r: LastResult): string {
  if (r.type === "perfect") {
    const sign = r.offsetMs >= 0 ? "+" : "";

    return `★ PARFAIT (${sign}${Math.round(r.offsetMs)} ms)`;
  }

  if (r.type === "hit") {
    const sign = r.offsetMs >= 0 ? "+" : "";

    return `✓ JUSTE (${sign}${Math.round(r.offsetMs)} ms)`;
  }

  if (r.reason === "no_press") {
    return "✗ AUCUNE FRAPPE, accord non reconnu sur le beat";
  }

  if (r.reason === "wrong_chord") {
    return "✗ MAUVAIS ACCORD, vérifie le renversement (note de basse)";
  }

  if (r.reason === "early") {
    return `↑ TROP TÔT (${Math.round(r.offsetMs ?? 0)} ms)`;
  }

  return `↓ TROP TARD (+${Math.round(r.offsetMs ?? 0)} ms)`;
}
